import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Cuenta = {
  id: string;
  clave: string;
  nombre: string;
  banco: string;
  numero_cuenta?: string;
  clabe?: string;
  moneda: string;
  saldo_inicial: number;
  saldo_actual: number;
  tipo: string;
  activo: boolean;
};

type Movimiento = {
  id: string;
  fecha: string;
  tipo: 'cargo' | 'abono';
  importe: number;
  referencia?: string;
  concepto?: string;
  origen_tipo?: string;
  conciliado_en?: string;
};

export function BancosPage() {
  const { data: cuentas } = useQuery({
    queryKey: ['bancos', 'cuentas'],
    queryFn: () => api<Cuenta[]>('/bancos/cuentas'),
  });
  const [sel, setSel] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Tesorería · Bancos</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Cuentas bancarias</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Cada pago a CxP genera un movimiento bancario vinculado a su póliza.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {(cuentas ?? []).map((c) => (
          <CuentaCard key={c.id} c={c} onSelect={() => setSel(c.id)} active={sel === c.id} />
        ))}
      </div>

      {sel && <Extracto cuentaID={sel} cuentas={cuentas ?? []} />}
    </div>
  );
}

function CuentaCard({ c, onSelect, active }: { c: Cuenta; onSelect: () => void; active: boolean }) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex w-full flex-col rounded-lg border bg-surface p-5 text-left shadow-card transition-all hover:shadow-lift",
        active ? "border-brand-500" : "border-border"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-brand-900/40">
            <Landmark size={16} />
          </div>
          <div>
            <div className="num text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">{c.clave}</div>
            <div className="text-[14px] font-semibold text-ink">{c.nombre}</div>
          </div>
        </div>
        <span className="rounded border border-border bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
          {c.tipo}
        </span>
      </div>
      <div className="mt-5">
        <div className="num text-[28px] font-semibold leading-none text-ink">{fmt.money(c.saldo_actual)}</div>
        <div className="mt-1 text-[12px] text-ink-mute">
          {c.banco}{c.numero_cuenta ? ` · ${c.numero_cuenta}` : ''}
        </div>
      </div>
    </button>
  );
}

function Extracto({ cuentaID, cuentas }: { cuentaID: string; cuentas: Cuenta[] }) {
  const { data: movs, isLoading } = useQuery({
    queryKey: ['bancos', 'movimientos', cuentaID],
    queryFn: () => api<Movimiento[]>(`/bancos/cuentas/${cuentaID}/movimientos`),
  });
  const c = cuentas.find((x) => x.id === cuentaID);
  return (
    <Card>
      <CardHeader title={`Extracto · ${c?.nombre}`} subtitle={c?.clabe ? `CLABE ${c.clabe}` : undefined} />
      <CardBody className="p-0">
        {isLoading ? (
          <div className="p-10 text-center text-[13px] text-ink-mute">Cargando movimientos…</div>
        ) : !movs || movs.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-ink-mute">Sin movimientos registrados aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Fecha</th>
                  <th className="px-5 py-3">Concepto</th>
                  <th className="px-5 py-3">Ref.</th>
                  <th className="px-5 py-3 text-right">Cargo</th>
                  <th className="px-5 py-3 text-right">Abono</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {movs.map((m) => (
                  <tr key={m.id} className="transition-colors hover:bg-muted/50">
                    <td className="num px-5 py-3 text-ink-mute">{fmt.date(m.fecha)}</td>
                    <td className="px-5 py-3">{m.concepto ?? m.origen_tipo ?? '—'}</td>
                    <td className="num px-5 py-3 text-[11px] text-ink-mute">{m.referencia ?? '—'}</td>
                    <td className="num px-5 py-3 text-right">
                      {m.tipo === 'cargo' ? (
                        <span className="inline-flex items-center gap-1 text-rose-700">
                          <ArrowUpRight size={12} />{fmt.money(m.importe)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="num px-5 py-3 text-right">
                      {m.tipo === 'abono' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <ArrowDownRight size={12} />{fmt.money(m.importe)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
